import React, { useState, useEffect, useMemo } from 'react';
import cn from 'classnames';
import { Container, Row, Col, Nav, NavLink, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import styles from './Stats.module.scss';
import Box from '../../common/Box';
import Spinner from '../../common/Spinner';
import ValuesList from '../../common/ValuesList';
import CircularProgressValue from '../../common/CircularProgressValue';
import Toolbar from '../../common/Toolbar';
import { getRate, getAvgTimeToShip } from '../../../utils/statsHelper';
import LineGraph from '../../graphs/LineGraph';
import { getGraphData, getBusinessData } from '../../../utils/statsHelper';
import {
  STAT_CANCELLATION_RATE,
  STAT_AVG_TIME_TO_SHIP,
  STAT_SHIP_ON_TIME_RATE,
  STAT_GMV,
  AVG_TIME_TO_SHIP_THRESHOLD,
  SHIP_ON_TIME_RATE_THRESHOLD,
  CANCELLATION_RATE_THRESHOLD,
} from '../../../constants/stats';

const BEST_SELLER_TAB_BY_PRODUCT = 'by_product';

const CHARTS_TAB_ORDERS = 'orders';
const CHARTS_TAB_GMV = 'gmv';

const CHARTS_TITLE_ORDERS = 'Orders';
const CHARTS_TITLE_GMV = 'GMV';

const IMAGE_HEIGHT = '35%';
const IMAGE_WIDTH = '35%';

const IMAGE_COLUMN_WIDTH = '30%';
const MODEL_COLUMN_WIDTH = '50%';
const QUANTITY_COLUMN_WIDTH = '20%';

const BEST_SELLERS_COLUMNS = [
  { name: 'Image', width: IMAGE_COLUMN_WIDTH },
  { name: 'Product', width: MODEL_COLUMN_WIDTH },
  { name: 'Quantity', width: QUANTITY_COLUMN_WIDTH },
];

function Stats(props) {
  const { data, startDate, endDate } = props;

  const [chartsTab, setChartsTab] = useState(CHARTS_TAB_ORDERS);
  const [graphData, setGraphData] = useState(null);

  useEffect(() => {
    if (data[STAT_GMV] && data[STAT_GMV].chartData) {
      const graphDataParams = {
        chartData: data[STAT_GMV].chartData,
        startDate,
        endDate,
      };

      if (chartsTab === CHARTS_TAB_ORDERS) {
        graphDataParams.itemCallback = (item) => Number(item.orderCount);
        graphDataParams.title = CHARTS_TITLE_ORDERS;
      } else {
        graphDataParams.itemCallback = (item) => Math.round(Number(item.usdPrice) / 100);
        graphDataParams.title = CHARTS_TITLE_GMV;
      }
      setGraphData(getGraphData(graphDataParams));
    }
  }, [data, startDate, endDate, chartsTab]);

  const businessData = useMemo(() => getBusinessData(data), [data]);

  const avgTimeToShip = data[STAT_AVG_TIME_TO_SHIP] ? getAvgTimeToShip(data[STAT_AVG_TIME_TO_SHIP]) : null;
  const shipOnTimeRate = data[STAT_SHIP_ON_TIME_RATE]
    ? getRate(data[STAT_SHIP_ON_TIME_RATE].shipOnTimeCount, data[STAT_SHIP_ON_TIME_RATE].orderCount)
    : null;
  const cancellationRate = data[STAT_CANCELLATION_RATE]
    ? getRate(data[STAT_CANCELLATION_RATE].count, data[STAT_CANCELLATION_RATE].totalCount)
    : null;

  const bestSellers = data?.best_sellers?.length ? data.best_sellers : [];
  const BestSellerTableHead = () => (
    <thead>
      <tr>
        {BEST_SELLERS_COLUMNS.map((column) => (
          <th key={column.name} width={column.width}>
            {column.name}
          </th>
        ))}
      </tr>
    </thead>
  );

  const BestSellerTableBody = () => (
    <tbody>
      {bestSellers.map((bestSeller) => (
        <tr key={bestSeller.model}>
          <td>
            <img width={IMAGE_WIDTH} height={IMAGE_HEIGHT} src={bestSeller.image} alt={`${bestSeller.model} preview`} />
          </td>
          <td>{bestSeller.model}</td>
          <td>{bestSeller.quantity}</td>
        </tr>
      ))}
    </tbody>
  );

  const BestSellersTable = () => (
    <Table>
      <BestSellerTableHead />
      <BestSellerTableBody />
    </Table>
  );

  return (
    <Container className={styles.statsContainer} fluid>
      <Row>
        <Col xs={9}>
          <Box toolbar="KPIs" className={styles.kpisBox}>
            <Row>
              {data.avg_time_to_ship && (
                <Col>
                  <CircularProgressValue
                    title="Average Time to Ship"
                    value={avgTimeToShip}
                    icon={<FontAwesomeIcon icon={faClock} />}
                    passed={avgTimeToShip < AVG_TIME_TO_SHIP_THRESHOLD}
                    data-testid="avg-time-to-ship"
                    suffix=" Hours"
                  />
                </Col>
              )}

              {data[STAT_SHIP_ON_TIME_RATE] && (
                <Col>
                  <CircularProgressValue
                    title="Ship On Time Rate"
                    value={shipOnTimeRate}
                    icon={<FontAwesomeIcon icon={faTruck} />}
                    passed={shipOnTimeRate > SHIP_ON_TIME_RATE_THRESHOLD}
                    data-testid="ship-on-time-rate"
                    suffix="%"
                  />
                </Col>
              )}

              {data[STAT_CANCELLATION_RATE] && (
                <Col>
                  <CircularProgressValue
                    title="Cancellation Rate"
                    value={cancellationRate}
                    icon={<FontAwesomeIcon icon={faTimes} />}
                    passed={cancellationRate < CANCELLATION_RATE_THRESHOLD}
                    invert={true}
                    data-testid="cancellation-rate"
                    suffix="%"
                  />
                </Col>
              )}
            </Row>
          </Box>
        </Col>

        <Col xs={3}>
          <Box toolbar="Business" className={styles.kpisBox}>
            {data ? <ValuesList values={businessData} data-testid="business-data" /> : <Spinner />}
          </Box>
        </Col>
      </Row>

      <Row>
        <Col xs={4}>
          <Toolbar name="Best Sellers" />

          <Toolbar color="transparent" margin_bottom>
            <Nav>
              <Nav.Item key={BEST_SELLER_TAB_BY_PRODUCT}>
                <NavLink active>BY PRODUCT</NavLink>
              </Nav.Item>
            </Nav>
          </Toolbar>

          <Box className={styles.chartsBox}>{data ? <BestSellersTable /> : <Spinner />}</Box>
        </Col>

        <Col xs={8}>
          <Toolbar name="Charts" />

          <Toolbar color="transparent" margin_bottom>
            <Nav>
              <Nav.Item key={CHARTS_TAB_ORDERS}>
                <NavLink onClick={() => setChartsTab(CHARTS_TAB_ORDERS)} active={chartsTab === CHARTS_TAB_ORDERS}>
                  {CHARTS_TITLE_ORDERS}
                </NavLink>

                <NavLink onClick={() => setChartsTab(CHARTS_TAB_GMV)} active={chartsTab === CHARTS_TAB_GMV}>
                  {CHARTS_TITLE_GMV}
                </NavLink>
              </Nav.Item>
            </Nav>
          </Toolbar>

          <Box className={cn(styles.chartsBox, graphData ? '' : styles.emptyChartsBox)}>
            {data ? (
              graphData ? (
                <LineGraph
                  data={graphData}
                  isCurrency={chartsTab === CHARTS_TAB_GMV}
                  graphTitle={chartsTab === CHARTS_TAB_ORDERS ? CHARTS_TITLE_ORDERS : CHARTS_TITLE_GMV}
                />
              ) : (
                <div className={styles.noData} data-testid="charts-no-data">
                  <h3>No data available</h3>
                </div>
              )
            ) : (
              <Spinner />
            )}
          </Box>
        </Col>
      </Row>
    </Container>
  );
}

export default Stats;
